import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, WarningCircle, ChatCircleText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { IncidentReportDialog } from "../components/IncidentReportDialog";

const STATUS_STYLES = {
  Open: "bg-[#C0392B]/10 text-[#C0392B] border-[#C0392B]/30",
  "Under Review": "bg-[#D68910]/10 text-[#9a6a0b] border-[#D68910]/40",
  Resolved: "bg-[#27AE60]/10 text-[#1e8449] border-[#27AE60]/30",
};

export default function IncidentsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [records, setRecords] = useState([]);
  const [resolving, setResolving] = useState(null);
  const [resolution, setResolution] = useState("");

  const load = () => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch((e) => { toast.error(formatApiError(e)); navigate("/"); });
    api.get(`/clients/${id}/incidents`).then((r) => setRecords(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (rid, status, res = "") => {
    try {
      await api.put(`/clients/${id}/incidents/${rid}`, { status, resolution: res });
      toast.success(`Marked ${status.toLowerCase()}`);
      setResolving(null);
      setResolution("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [reports, setReports] = useState({});
  const disseminate = async (rid) => {
    try {
      const { data } = await api.post(`/clients/${id}/incidents/${rid}/disseminate`, {});
      const rep = data.report || {};
      setReports((m) => ({ ...m, [rid]: rep }));
      toast.success(rep.critical
        ? `Compliant report filed — CRITICAL incident (CHC): EIM entry due ${rep.eim_due}`
        : rep.serious
          ? "Compliant report filed — agency-serious: internal policy timelines apply, no EIM (not CHC)"
          : "Compliant report filed — standard incident", { duration: 7000 });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!client) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  return (
    <div className="max-w-3xl" data-testid="incidents-page">
      <button onClick={() => navigate(`/clients/${id}`)} className="mb-4 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="back-to-client">
        <ArrowLeft size={14} /> Back to client
      </button>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Incidents & Complaints</h1>
          <span className="rounded-lg border border-stone-200 bg-accent px-3 py-1 text-sm font-semibold text-accent-foreground">{client.forms?.intake?.full_name || client.client_code}</span>
        </div>
        <IncidentReportDialog clientId={id} onReported={load} buttonTestId="incidents-report-button" />
      </div>

      <div className="space-y-3" data-testid="incidents-list">
        {records.length === 0 && (
          <p className="rounded-lg border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500">No incidents or complaints filed.</p>
        )}
        {records.map((r) => (
          <div key={r.id} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`incident-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`flex h-7 w-7 items-center justify-center rounded-md ${r.kind === "incident" ? "bg-[#C0392B]/10 text-[#C0392B]" : "bg-[#D68910]/10 text-[#9a6a0b]"}`}>
                {r.kind === "incident" ? <WarningCircle size={16} /> : <ChatCircleText size={16} />}
              </span>
              <p className="font-heading text-sm font-semibold text-stone-900">{r.category}</p>
              <Badge variant="outline" className={r.kind === "incident" ? "border-stone-300 text-stone-500" : "border-stone-300 text-stone-500"}>{r.kind}</Badge>
              <Badge variant="outline" className={STATUS_STYLES[r.status]} data-testid={`incident-status-${r.id}`}>{r.status}</Badge>
              <span className="ml-auto text-xs text-stone-400">{r.date} · {r.reported_by}</span>
            </div>
            {r.description && <p className="mt-2 whitespace-pre-wrap text-sm text-stone-700">{r.description}</p>}
            {r.report_id && (
              <p className="mt-2 rounded-lg border border-primary/25 bg-primary/[0.05] px-3 py-2 text-xs font-medium text-primary" data-testid={`incident-report-filed-${r.id}`}>
                Compliant incident report filed{(reports[r.id] || {}).critical_basis ? ` — ${reports[r.id].critical_basis}` : ""} · tracked under Quality → Incidents &amp; Complaints
              </p>
            )}
            {r.resolution && <p className="mt-2 rounded-lg bg-accent/60 px-3 py-2 text-xs text-stone-600"><strong>Resolution:</strong> {r.resolution}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              {!r.report_id && (
                <Button variant="outline" size="sm" onClick={() => disseminate(r.id)} className="border-primary/40 text-primary hover:bg-primary hover:text-white" data-testid={`incident-disseminate-${r.id}`}>
                  File compliant report
                </Button>
              )}
              {r.status === "Open" && (
                <Button variant="outline" size="sm" onClick={() => setStatus(r.id, "Under Review")} className="border-stone-300 text-stone-700 hover:bg-accent" data-testid={`incident-review-${r.id}`}>
                  Start Review
                </Button>
              )}
              {r.status !== "Resolved" && (
                <Button variant="outline" size="sm" onClick={() => setResolving(resolving === r.id ? null : r.id)} className="border-stone-300 text-stone-700 hover:bg-primary hover:text-white" data-testid={`incident-resolve-${r.id}`}>
                  Resolve
                </Button>
              )}
            </div>
            {resolving === r.id && (
              <div className="mt-3 flex gap-2">
                <Textarea rows={2} placeholder="Resolution notes..." value={resolution} onChange={(e) => setResolution(e.target.value)}
                  className="border-stone-300 bg-white text-sm" data-testid={`incident-resolution-${r.id}`} />
                <Button size="sm" onClick={() => setStatus(r.id, "Resolved", resolution)} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`incident-resolve-confirm-${r.id}`}>
                  Confirm
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
